import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

import { API_URL as API_BASE } from '../config/api';

const OurPartners = () => {
    const navigate = useNavigate();

    //  Search state 
    const [query,       setQuery]       = useState('');
    const [results,     setResults]     = useState({ formations: [], sessions: [], certifications: [] });
    const [loading,     setLoading]     = useState(false);
    const [showDropdown,setShowDropdown]= useState(false);
    const [highlighted, setHighlighted] = useState(-1);
    const inputRef  = useRef(null);
    const dropRef   = useRef(null);
    const timerRef  = useRef(null);

    //  ── RECHERCHE UNIFIÉE : Formations + Sessions + Certifications ──────────
    const search = useCallback(async (q) => {
        if (!q.trim()) { 
            setResults({ formations: [], sessions: [], certifications: [] }); 
            setShowDropdown(false); 
            return; 
        }
        setLoading(true);
        try {
            // Recherche en parallèle dans les 3 sources
            const [formationsRes, sessionsRes, certificationsRes] = await Promise.allSettled([
                axios.get(`${API_BASE}/formations`, { params: { search: q } }),
                axios.get(`${API_BASE}/formations/sessions/public`, { params: { search: q } }),
                axios.get(`${API_BASE}/formations/certifications/public`, { params: { search: q } })
            ]);

            // Extraire les données avec gestion d'erreur robuste
            const formations = formationsRes.status === 'fulfilled' 
                ? (Array.isArray(formationsRes.value.data) ? formationsRes.value.data : (formationsRes.value.data.data || []))
                : [];

            const sessions = sessionsRes.status === 'fulfilled'
                ? (Array.isArray(sessionsRes.value.data) ? sessionsRes.value.data : (sessionsRes.value.data.data || []))
                : [];

            const certifications = certificationsRes.status === 'fulfilled'
                ? (Array.isArray(certificationsRes.value.data) ? certificationsRes.value.data : (certificationsRes.value.data.data || []))
                : [];

            // Filtrer et limiter les résultats
            const filterByQuery = (item) => {
                const searchLower = q.toLowerCase();
                return (
                    item.title?.toLowerCase().includes(searchLower) ||
                    item.titre?.toLowerCase().includes(searchLower) ||
                    item.domain?.toLowerCase().includes(searchLower) ||
                    item.domaine?.toLowerCase().includes(searchLower) ||
                    item.description?.toLowerCase().includes(searchLower)
                );
            };

            setResults({
                formations: formations.filter(filterByQuery).slice(0, 5),
                sessions: sessions.filter(filterByQuery).slice(0, 4),
                certifications: certifications.filter(filterByQuery).slice(0, 4)
            });
            setShowDropdown(true);
            setHighlighted(-1);
        } catch (error) {
            console.error('Erreur recherche:', error);
            setResults({ formations: [], sessions: [], certifications: [] });
        } finally {
            setLoading(false);
        }
    }, []);

    //  Debounce 
    useEffect(() => {
        clearTimeout(timerRef.current);
        if (query.trim().length >= 2) {
            timerRef.current = setTimeout(() => search(query), 350);
        } else {
            setResults({ formations: [], sessions: [], certifications: [] });
            setShowDropdown(false);
        }
        return () => clearTimeout(timerRef.current);
    }, [query, search]);

    //  Clic hors dropdown
    useEffect(() => {
        const handler = (e) => {
            if (!dropRef.current?.contains(e.target) && !inputRef.current?.contains(e.target)) {
                setShowDropdown(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    // ── Créer une liste aplatie des résultats pour la navigation clavier ──────
    const getAllResults = () => {
        const all = [];
        results.formations.forEach(f => all.push({ type: 'formation', data: f }));
        results.sessions.forEach(s => all.push({ type: 'session', data: s }));
        results.certifications.forEach(c => all.push({ type: 'certification', data: c }));
        return all;
    };

    //  Navigation clavier
    const handleKeyDown = (e) => {
        if (!showDropdown) return;
        const allResults = getAllResults();
        
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setHighlighted(h => Math.min(h + 1, allResults.length - 1));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setHighlighted(h => Math.max(h - 1, -1));
        } else if (e.key === 'Enter') {
            e.preventDefault();
            if (highlighted >= 0 && allResults[highlighted]) {
                handleResultClick(allResults[highlighted].type, allResults[highlighted].data);
            } else if (query.trim()) {
                handleSearchSubmit();
            }
        } else if (e.key === 'Escape') {
            setShowDropdown(false);
        }
    };

    // ── Gérer le clic sur un résultat selon son type ──────────────────────────
    const handleResultClick = (type, item) => {
        setShowDropdown(false);
        setQuery('');
        
        switch (type) {
            case 'formation':
                navigate(`/formations/${item.slug}`);
                break;
            case 'session':
                navigate('/sessions-en-cours');
                break;
            case 'certification':
                navigate('/certifications-cinef');
                break;
            default:
                navigate(`/formations/${item.slug}`);
        }
    };

    // ── Submisson recherche → page de recherche ───────────────────────────────
    const handleSearchSubmit = () => {
        if (!query.trim()) return;
        setShowDropdown(false);
        navigate(`/recherche?q=${encodeURIComponent(query.trim())}`);
    };

    //  Highlight du texte trouvé 
    const highlight = (text, q) => {
        if (!q.trim() || !text) return text;
        const regex = new RegExp(`(${q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
        const parts = text.split(regex);
        return parts.map((part, i) =>
            regex.test(part)
                ? <mark key={i} style={{ background: 'rgba(253,126,20,0.2)', color: '#fd7e14', borderRadius: 3, padding: '0 2px' }}>{part}</mark>
                : part
        );
    };

    //  Domaine → couleur 
    const domainColor = (domain) => {
        const map = {
            'management':   '#198754',
            'informatique': '#0d6efd',
            'finance':      '#6f42c1',
            'rh':           '#fd7e14',
            'marketing':    '#dc3545',
            'droit':        '#20c997',
            'qualite':      '#ffc107',
        };
        const key = (domain || '').toLowerCase();
        for (const [k, v] of Object.entries(map)) {
            if (key.includes(k)) return v;
        }
        return '#fd7e14';
    };

    //  Partenaires
    const partners = [
        { id:1,  name:'Partenaire 1',  logo:'../assets/images/partenaires/ben1.jpg'   },
        { id:2,  name:'Partenaire 2',  logo:'../assets/images/partenaires/ben2.png'   },
        { id:3,  name:'Partenaire 3',  logo:'../assets/images/partenaires/ben3.png'   },
        { id:4,  name:'Partenaire 4',  logo:'../assets/images/partenaires/ben4.png'   },
        { id:5,  name:'Partenaire 5',  logo:'../assets/images/partenaires/ben5.png'   },
        { id:6,  name:'Partenaire 6',  logo:'../assets/images/partenaires/ben6.png'   },
        { id:7,  name:'Partenaire 7',  logo:'../assets/images/partenaires/ben7.png'   },
        { id:8,  name:'Partenaire 8',  logo:'../assets/images/partenaires/ben8.png'   },
        { id:9,  name:'Partenaire 9',  logo:'../assets/images/partenaires/ben9.png'   },
        { id:10, name:'Partenaire 10', logo:'../assets/images/partenaires/ben10.png'  },
        { id:11, name:'Partenaire 11', logo:'../assets/images/partenaires/ben11.png'  },
        { id:12, name:'Partenaire 12', logo:'../assets/images/partenaires/ben12.png'  },
        { id:13, name:'Partenaire 13', logo:'../assets/images/partenaires/ben13.png'  },
        { id:14, name:'Partenaire 14', logo:'../assets/images/partenaires/ben14.png'  },
        { id:15, name:'Partenaire 15', logo:'../assets/images/partenaires/ben15.png'  },
        { id:16, name:'Partenaire 16', logo:'../assets/images/partenaires/camer1.png' },
        { id:17, name:'Partenaire 17', logo:'../assets/images/partenaires/centr1.png' },
        { id:18, name:'Partenaire 18', logo:'../assets/images/partenaires/civ1.png'   },
        { id:19, name:'Partenaire 19', logo:'../assets/images/partenaires/civ2.png'   },
        { id:20, name:'Partenaire 20', logo:'../assets/images/partenaires/civ3.png'   },
        { id:21, name:'Partenaire 21', logo:'../assets/images/partenaires/civ4.png'   },
        { id:22, name:'Partenaire 22', logo:'../assets/images/partenaires/congo1.png' },
        { id:23, name:'Partenaire 23', logo:'../assets/images/partenaires/congo2.png' },
        { id:24, name:'Partenaire 24', logo:'../assets/images/partenaires/gabo1.png'  },
        { id:25, name:'Partenaire 25', logo:'../assets/images/partenaires/gabo2.png'  },
        { id:26, name:'Partenaire 26', logo:'../assets/images/partenaires/gabo3.png'  },
        { id:27, name:'Partenaire 27', logo:'../assets/images/partenaires/guin1.png'  },
        { id:28, name:'Partenaire 28', logo:'../assets/images/partenaires/mal1.png'   },
        { id:29, name:'Partenaire 29', logo:'../assets/images/partenaires/mal2.png'   },
        { id:30, name:'Partenaire 30', logo:'../assets/images/partenaires/mal3.png'   },
        { id:31, name:'Partenaire 31', logo:'../assets/images/partenaires/mal4.png'   },
        { id:32, name:'Partenaire 32', logo:'../assets/images/partenaires/mal5.png'   },
        { id:33, name:'Partenaire 33', logo:'../assets/images/partenaires/mal7.png'   },
        { id:34, name:'Partenaire 34', logo:'../assets/images/partenaires/nig1.png'   },
        { id:35, name:'Partenaire 35', logo:'../assets/images/partenaires/nig2.png'   },
        { id:36, name:'Partenaire 36', logo:'../assets/images/partenaires/nig3.png'   },
        { id:37, name:'Partenaire 37', logo:'../assets/images/partenaires/rdc1.png'   },
        { id:38, name:'Partenaire 38', logo:'../assets/images/partenaires/rdc2.png'   },
        { id:39, name:'Partenaire 39', logo:'../assets/images/partenaires/sene1.png'  },
        { id:40, name:'Partenaire 40', logo:'../assets/images/partenaires/tchad1.png' },
        { id:41, name:'Partenaire 41', logo:'../assets/images/partenaires/tog1.png'   },
        { id:42, name:'Partenaire 42', logo:'../assets/images/partenaires/tog2.png'   },
        { id:43, name:'Partenaire 43', logo:'../assets/images/partenaires/tog3.png'   },
        { id:44, name:'Partenaire 44', logo:'../assets/images/partenaires/tog4.png'   },
        { id:45, name:'Partenaire 45', logo:'../assets/images/partenaires/tog5.png'   },
        { id:46, name:'Partenaire 46', logo:'../assets/images/partenaires/tog6.png'   },
        { id:47, name:'Partenaire 47', logo:'../assets/images/partenaires/tog7.png'   },
        { id:48, name:'Partenaire 48', logo:'../assets/images/partenaires/tog8.png'   },
        { id:49, name:'Partenaire 49', logo:'../assets/images/partenaires/tog9.png'   },
        { id:50, name:'Partenaire 50', logo:'../assets/images/partenaires/tog10.png'  },
        { id:51, name:'Partenaire 51', logo:'../assets/images/partenaires/tog11.png'  },
        { id:52, name:'Partenaire 52', logo:'../assets/images/partenaires/tog12.png'  },
        { id:53, name:'Partenaire 53', logo:'../assets/images/partenaires/tog13.png'  },
        { id:54, name:'Partenaire 54', logo:'../assets/images/partenaires/tog14.png'  },
        { id:55, name:'Partenaire 55', logo:'../assets/images/partenaires/ben16.png'  },
        { id:56, name:'Partenaire 56', logo:'../assets/images/partenaires/ben17.png'  },
        { id:57, name:'Partenaire 57', logo:'../assets/images/partenaires/ben18.png'  },
        { id:59, name:'Partenaire 59', logo:'../assets/images/partenaires/ben19.png'  },
        { id:60, name:'Partenaire 60', logo:'../assets/images/partenaires/ben20.png'  },
        { id:61, name:'Partenaire 61', logo:'../assets/images/partenaires/ben21.png'  },
        { id:62, name:'Partenaire 62', logo:'../assets/images/partenaires/ben22.png'  },
        { id:63, name:'Partenaire 63', logo:'../assets/images/partenaires/ben23.png'  },
        { id:64, name:'Partenaire 64', logo:'../assets/images/partenaires/ben24.png'  },
        { id:65, name:'Partenaire 65', logo:'../assets/images/partenaires/ben25.png'  },
    ];

    const duplicatedPartners = [...partners, ...partners, ...partners];

    return (
        <section className="our-partners-section">
            <div className="container">

                {/*  Barre de recherche dynamique  */}
                <div className="search-bar-wrapper">
                    <div className="search-bar-container" style={{ position: 'relative' }}>
                        <div className="search-icon">
                            {loading ? (
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fd7e14" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ animation: 'spin .8s linear infinite' }}>
                                    <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
                                </svg>
                            ) : (
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
                                </svg>
                            )}
                        </div>

                        <input
                            ref={inputRef}
                            type="text"
                            className="search-input"
                            placeholder="Rechercher une formation, session en cours ou certification..."
                            value={query}
                            onChange={e => setQuery(e.target.value)}
                            onKeyDown={handleKeyDown}
                            onFocus={() => {
                                const totalResults = results.formations.length + results.sessions.length + results.certifications.length;
                                if (query.trim().length >= 2 && totalResults > 0) setShowDropdown(true);
                            }}
                            autoComplete="off"
                        />

                        {query && (
                            <button
                                onClick={() => { 
                                    setQuery(''); 
                                    setResults({ formations: [], sessions: [], certifications: [] }); 
                                    setShowDropdown(false); 
                                    inputRef.current?.focus(); 
                                }}
                                style={{ background:'none', border:'none', cursor:'pointer', padding:'0 8px', color:'#a0aec0', fontSize:18, lineHeight:1 }}
                                title="Effacer"
                            >×</button>
                        )}

                        {/* ── Bouton Rechercher → SearchPage ── */}
                        <button className="search-button" onClick={handleSearchSubmit}>
                            Rechercher
                        </button>
                    </div>

                    {/*  ── DROPDOWN RÉSULTATS UNIFIÉS ── */}
                    {showDropdown && (() => {
                        const totalResults = results.formations.length + results.sessions.length + results.certifications.length;
                        const allResults = getAllResults();

                        return (
                            <div ref={dropRef} className="search-dropdown">
                                {totalResults === 0 && !loading && (
                                    <div className="search-empty">
                                        <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#cbd5e0" strokeWidth="1.5">
                                            <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
                                        </svg>
                                        <p>Aucun résultat trouvé pour <strong>« {query} »</strong></p>
                                        <button
                                            onClick={handleSearchSubmit}
                                            style={{ marginTop:10, padding:'8px 16px', background:'#fff5ee', border:'1px solid #fd7e14', borderRadius:8, color:'#fd7e14', fontWeight:600, fontSize:13, cursor:'pointer' }}
                                        >
                                            Rechercher sur tout le site →
                                        </button>
                                    </div>
                                )}

                                {totalResults > 0 && (
                                    <>
                                        <div className="search-dropdown-header">
                                            <span>{totalResults} résultat{totalResults > 1 ? 's' : ''} trouvé{totalResults > 1 ? 's' : ''}</span>
                                        </div>

                                        {/* ── Formations ── */}
                                        {results.formations.length > 0 && (
                                            <div className="search-category">
                                                <div className="category-header">
                                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fd7e14" strokeWidth="2">
                                                        <path d="M22 10v6M2 10l10-5 10 5-10 5z"/><path d="M6 12v5c3 3 9 3 12 0v-5"/>
                                                    </svg>
                                                    <span>Formations ({results.formations.length})</span>
                                                </div>
                                                {results.formations.map((f, localIdx) => {
                                                    const globalIdx = allResults.findIndex(r => r.type === 'formation' && r.data.id === f.id);
                                                    const color = domainColor(f.domain || f.domaine || '');
                                                    return (
                                                        <div
                                                            key={`formation-${f.id}`}
                                                            className={`search-result-item ${highlighted === globalIdx ? 'highlighted' : ''}`}
                                                            onMouseEnter={() => setHighlighted(globalIdx)}
                                                            onClick={() => handleResultClick('formation', f)}
                                                        >
                                                            <div className="result-icon" style={{ background: color + '18', color }}>
                                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                                    <path d="M22 10v6M2 10l10-5 10 5-10 5z"/><path d="M6 12v5c3 3 9 3 12 0v-5"/>
                                                                </svg>
                                                            </div>
                                                            <div className="result-body">
                                                                <div className="result-title">{highlight(f.title || f.titre, query)}</div>
                                                                <div className="result-meta">
                                                                    {(f.domain || f.domaine) && (
                                                                        <span className="result-tag" style={{ background: color + '15', color }}>
                                                                            {f.domain || f.domaine}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                            <svg className="result-arrow" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                                <path d="M9 18l6-6-6-6"/>
                                                            </svg>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}

                                        {/* ── Sessions en cours ── */}
                                        {results.sessions.length > 0 && (
                                            <div className="search-category">
                                                <div className="category-header">
                                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#198754" strokeWidth="2">
                                                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><path d="M16 2v4M8 2v4M3 10h18"/>
                                                    </svg>
                                                    <span>Sessions en cours ({results.sessions.length})</span>
                                                </div>
                                                {results.sessions.map((s, localIdx) => {
                                                    const globalIdx = allResults.findIndex(r => r.type === 'session' && r.data.id === s.id);
                                                    return (
                                                        <div
                                                            key={`session-${s.id}`}
                                                            className={`search-result-item ${highlighted === globalIdx ? 'highlighted' : ''}`}
                                                            onMouseEnter={() => setHighlighted(globalIdx)}
                                                            onClick={() => handleResultClick('session', s)}
                                                        >
                                                            <div className="result-icon" style={{ background: '#19875418', color: '#198754' }}>
                                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><path d="M16 2v4M8 2v4M3 10h18"/>
                                                                </svg>
                                                            </div>
                                                            <div className="result-body">
                                                                <div className="result-title">{highlight(s.title || s.titre, query)}</div>
                                                                <div className="result-meta">
                                                                    <span className="result-tag" style={{ background: '#19875415', color: '#198754' }}>
                                                                        {s.startDate ? `Début: ${s.startDate.split('T')[0]}` : 'Session active'}
                                                                    </span>
                                                                    {s.city && (
                                                                        <span className="result-tag" style={{ background:'#f7fafc', color:'#718096' }}>
                                                                            {s.city}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                            <svg className="result-arrow" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                                <path d="M9 18l6-6-6-6"/>
                                                            </svg>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}

                                        {/* ── Certifications CINEF ── */}
                                        {results.certifications.length > 0 && (
                                            <div className="search-category">
                                                <div className="category-header">
                                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#20c997" strokeWidth="2">
                                                        <circle cx="12" cy="8" r="7"/><path d="M8.21 13.89L7 23l5-3 5 3-1.21-9.12"/>
                                                    </svg>
                                                    <span>Certifications CINEF ({results.certifications.length})</span>
                                                </div>
                                                {results.certifications.map((c, localIdx) => {
                                                    const globalIdx = allResults.findIndex(r => r.type === 'certification' && r.data.id === c.id);
                                                    return (
                                                        <div
                                                            key={`cert-${c.id}`}
                                                            className={`search-result-item ${highlighted === globalIdx ? 'highlighted' : ''}`}
                                                            onMouseEnter={() => setHighlighted(globalIdx)}
                                                            onClick={() => handleResultClick('certification', c)}
                                                        >
                                                            <div className="result-icon" style={{ background: '#20c99718', color: '#20c997' }}>
                                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                                    <circle cx="12" cy="8" r="7"/><path d="M8.21 13.89L7 23l5-3 5 3-1.21-9.12"/>
                                                                </svg>
                                                            </div>
                                                            <div className="result-body">
                                                                <div className="result-title">{highlight(c.title || c.titre, query)}</div>
                                                                <div className="result-meta">
                                                                    <span className="result-tag" style={{ background: '#20c99715', color: '#20c997' }}>
                                                                        {c.code || 'Certification CINEF'}
                                                                    </span>
                                                                    {c.duration && (
                                                                        <span className="result-tag" style={{ background:'#f7fafc', color:'#718096' }}>
                                                                            {c.duration}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                            <svg className="result-arrow" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                                <path d="M9 18l6-6-6-6"/>
                                                            </svg>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}

                                        {/* ── Footer "Voir tout" ── */}
                                        {/* <div className="search-footer" onClick={handleSearchSubmit}>
                                            <span>Voir tous les résultats pour « {query} »</span>
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                                <path d="M5 12h14M12 5l7 7-7 7"/>
                                            </svg>
                                        </div> */}
                                    </>
                                )}
                            </div>
                        );
                    })()}
                </div>

                {/*  En-tête section  */}
                <div className="section-header">
                    <h2 className="section-title">
                        Ils nous font confiance et <br />
                        <span className="title-gradient">nous accompagnent dans notre mission</span>
                    </h2>
                    <p className="section-subtitle">
                        Découvrez les entreprises et organisations qui font confiance à CINEF
                        pour le développement des compétences de leurs équipes
                    </p>
                </div>

                {/*  Carousel partenaires  */}
                <div className="partners-carousel-wrapper">
                    <div className="partners-carousel">
                        <div className="partners-track">
                            {duplicatedPartners.map((partner, index) => (
                                <div key={`${partner.id}-${index}`} className="partner-item">
                                    <div className="partner-logo-box">
                                        <img src={partner.logo} alt={partner.name} className="partner-logo" />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            <style jsx>{`
                @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');

                @keyframes spin { to { transform: rotate(360deg); } }
                @keyframes fadeInDown { from { opacity:0; transform:translateY(-30px); } to { opacity:1; transform:translateY(0); } }
                @keyframes fadeInUp   { from { opacity:0; transform:translateY(30px);  } to { opacity:1; transform:translateY(0); } }
                @keyframes dropIn     { from { opacity:0; transform:translateY(-8px);  } to { opacity:1; transform:translateY(0); } }
                @keyframes floatBubble { 0%,100%{transform:translate(0,0) scale(1)} 50%{transform:translate(30px,-30px) scale(1.1)} }
                @keyframes gradientShift { 0%,100%{background-position:0% 50%} 50%{background-position:100% 50%} }
                @keyframes scroll { 0%{transform:translateX(0)} 100%{transform:translateX(calc(-100% / 3))} }
                @keyframes ping { 0%,100%{transform:scale(1);opacity:1} 50%{transform:scale(1.3);opacity:.7} }

                *,*::before,*::after { margin:0; padding:0; box-sizing:border-box; }

                .our-partners-section {
                    padding: 100px 0;
                    margin-top: -60px;
                    overflow: visible;
                    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
                    position: relative;
                }

                .our-partners-section::before {
                    content:''; position:absolute; top:-10%; right:-5%;
                    width:400px; height:400px;
                    background:radial-gradient(circle,rgba(253,126,20,.05) 0%,transparent 70%);
                    border-radius:50%; animation:floatBubble 20s ease-in-out infinite;
                }
                .our-partners-section::after {
                    content:''; position:absolute; bottom:-10%; left:-5%;
                    width:350px; height:350px;
                    background:radial-gradient(circle,rgba(25,135,84,.05) 0%,transparent 70%);
                    border-radius:50%; animation:floatBubble 15s ease-in-out infinite reverse;
                }

                .container { max-width:1280px; margin:0 auto; padding:0 24px; position:relative; z-index:1; }

                /*  Search  */
                .search-bar-wrapper { margin:0 auto 60px; max-width:800px; position:relative; z-index:100; animation:fadeInUp .8s ease-out .6s backwards; }

                .search-bar-container {
                    display:flex; align-items:center; background:white;
                    border-radius:16px; box-shadow:0 8px 32px rgba(0,0,0,.08);
                    padding:8px; border:2px solid #e2e8f0; transition:all .3s ease;
                }
                .search-bar-container:focus-within {
                    border-color:#fd7e14;
                    box-shadow:0 12px 40px rgba(253,126,20,.15);
                    transform:translateY(-2px);
                }

                .search-icon { display:flex; align-items:center; justify-content:center; padding:0 16px; color:#718096; transition:color .3s; }
                .search-bar-container:focus-within .search-icon { color:#fd7e14; }

                .search-input { flex:1; border:none; outline:none; padding:16px 12px; font-size:16px; font-family:'Inter',sans-serif; color:#1a202c; background:transparent; }
                .search-input::placeholder { color:#a0aec0; }

                .search-button {
                    padding:14px 32px; background:linear-gradient(135deg,#fd7e14 0%,#dc3545 100%);
                    color:white; border:none; border-radius:12px; font-size:15px; font-weight:700;
                    cursor:pointer; transition:all .3s; font-family:'Inter',sans-serif; white-space:nowrap;
                }
                .search-button:hover { transform:translateY(-2px); box-shadow:0 8px 24px rgba(253,126,20,.35); }
                .search-button:active { transform:translateY(0); }

                /*  Dropdown  */
                .search-dropdown {
                    position:absolute; top:calc(100% + 10px); left:0; right:0;
                    background:white; border-radius:16px;
                    box-shadow:0 20px 60px rgba(0,0,0,.12), 0 4px 16px rgba(0,0,0,.06);
                    border:1px solid #e2e8f0; overflow:hidden;
                    animation:dropIn .2s ease-out;
                    z-index:200;
                }

                .search-dropdown-header {
                    padding:10px 18px; background:#f7fafc;
                    border-bottom:1px solid #e2e8f0;
                    font-size:11px; font-weight:700; text-transform:uppercase;
                    letter-spacing:.8px; color:#718096;
                }

                .search-category {
                    padding:8px 0;
                }
                .search-category + .search-category {
                    border-top:1px solid #e2e8f0;
                }
                .category-header {
                    display:flex; align-items:center; gap:8px;
                    padding:10px 18px; font-size:11px; font-weight:700;
                    text-transform:uppercase; letter-spacing:.8px;
                    color:#4a5568; background:#f9fafb;
                }

                .search-result-item {
                    display:flex; align-items:flex-start; gap:14px;
                    padding:14px 18px; cursor:pointer;
                    transition:background .15s;
                    border-bottom:1px solid #f7fafc;
                }
                .search-result-item:last-of-type { border-bottom:none; }
                .search-result-item.highlighted,
                .search-result-item:hover { background:#fef9f5; }

                .result-icon {
                    width:38px; height:38px; border-radius:10px;
                    display:flex; align-items:center; justify-content:center;
                    flex-shrink:0; margin-top:2px;
                }

                .result-body { flex:1; min-width:0; }
                .result-title { font-size:14.5px; font-weight:600; color:#1a202c; margin-bottom:6px; }
                .result-meta  { display:flex; gap:6px; flex-wrap:wrap; margin-bottom:5px; }
                .result-tag   { font-size:11px; font-weight:600; padding:2px 8px; border-radius:20px; }
                .result-desc  { font-size:12.5px; color:#718096; line-height:1.5; }

                .result-arrow { color:#cbd5e0; flex-shrink:0; margin-top:10px; transition:transform .15s, color .15s; }
                .search-result-item.highlighted .result-arrow,
                .search-result-item:hover .result-arrow { color:#fd7e14; transform:translateX(3px); }

                .search-empty {
                    padding:40px 20px; text-align:center; color:#a0aec0;
                    display:flex; flex-direction:column; align-items:center; gap:12px;
                }
                .search-empty p { font-size:14px; color:#718096; }
                .search-empty strong { color:#1a202c; }

                .search-footer {
                    display:flex; align-items:center; justify-content:center; gap:8px;
                    padding:12px 18px; background:#f7fafc; border-top:1px solid #e2e8f0;
                    font-size:13px; font-weight:600; color:#fd7e14; cursor:pointer;
                    transition:background .15s;
                }
                .search-footer:hover { background:#fff5ee; }

                /*  Section header  */
                .section-header { text-align:center; margin-bottom:60px; }
                .section-title { font-size:42px; font-weight:900; color:#1a202c; margin-bottom:16px; letter-spacing:-.5px; line-height:1.2; animation:fadeInUp .8s ease-out .2s backwards; }
                .title-gradient {
                    background:linear-gradient(135deg,#fd7e14 0%,#dc3545 50%,#198754 100%);
                    -webkit-background-clip:text; -webkit-text-fill-color:transparent; background-clip:text;
                    animation:gradientShift 5s ease infinite; background-size:200% 200%;
                }
                .section-subtitle { font-size:18px; color:#718096; max-width:700px; margin:0 auto; line-height:1.7; animation:fadeInUp .8s ease-out .4s backwards; }

                /*  Carousel  */
                .partners-carousel-wrapper { position:relative; width:100%; overflow:hidden; padding:20px 0; }
                .partners-carousel-wrapper::before,.partners-carousel-wrapper::after { content:''; position:absolute; top:0; width:200px; height:100%; z-index:2; pointer-events:none; }
                .partners-carousel-wrapper::before { left:0; background:linear-gradient(to right,#ffffff,transparent); }
                .partners-carousel-wrapper::after  { right:0; background:linear-gradient(to left,#f8f9fa,transparent); }
                .partners-carousel { position:relative; width:100%; }
                .partners-track { display:flex; gap:32px; animation:scroll 120s linear infinite; width:fit-content; }
                .partners-track:hover { animation-play-state:paused; }
                .partner-item { flex-shrink:0; width:220px; }
                .partner-logo-box {
                    background:white; border-radius:20px; padding:32px 24px; height:140px;
                    display:flex; align-items:center; justify-content:center;
                    box-shadow:0 4px 20px rgba(0,0,0,.06); transition:all .4s cubic-bezier(.4,0,.2,1);
                    border:2px solid transparent; position:relative; overflow:hidden;
                }
                .partner-logo-box::before {
                    content:''; position:absolute; top:-50%; left:-50%; width:200%; height:200%;
                    background:linear-gradient(45deg,transparent 30%,rgba(253,126,20,.1) 50%,transparent 70%);
                    transform:translateX(-100%) translateY(-100%); transition:transform .6s;
                }
                .partner-logo-box:hover::before { transform:translateX(100%) translateY(100%); }
                .partner-logo-box:hover { transform:translateY(-12px) scale(1.05); box-shadow:0 16px 48px rgba(0,0,0,.12); border-color:#fd7e14; background:linear-gradient(135deg,#ffffff 0%,#fff9f5 100%); }
                .partner-logo { max-width:100%; max-height:100%; object-fit:contain; filter:none; opacity:.85; transition:all .4s cubic-bezier(.4,0,.2,1); }
                .partner-logo-box:hover .partner-logo { opacity:1; transform:scale(1.1); }

                /*  Responsive  */
                @media(max-width:968px){
                    .our-partners-section{padding:80px 0;}
                    .section-title{font-size:32px;}
                    .section-subtitle{font-size:16px;}
                    .search-bar-wrapper{max-width:90%;}
                    .search-input{font-size:15px;padding:14px 10px;}
                    .search-button{padding:12px 24px;font-size:14px;}
                    .partners-track{gap:24px;animation:scroll 90s linear infinite;}
                    .partner-item{width:180px;}
                    .partner-logo-box{height:120px;padding:24px 20px;}
                }
                @media(max-width:640px){
                    .our-partners-section{padding:60px 0;}
                    .section-title{font-size:28px;}
                    .section-subtitle{font-size:15px;}
                    .search-bar-wrapper{margin-bottom:40px;}
                    .search-bar-container{flex-direction:column;gap:8px;}
                    .search-icon{position:absolute;left:16px;top:16px;}
                    .search-input{width:100%;padding:16px 16px 16px 48px;}
                    .search-button{width:100%;padding:14px;}
                    .partners-track{gap:20px;animation:scroll 70s linear infinite;}
                    .partner-item{width:160px;}
                    .partner-logo-box{height:100px;padding:20px 16px;border-radius:16px;}
                    .partners-carousel-wrapper::before,.partners-carousel-wrapper::after{width:80px;}
                    .search-dropdown{border-radius:12px;}
                }
            `}</style>
        </section>
    );
};

export default OurPartners;